// src/pages/public/usePublicSeoData.ts
import { useEffect, useState } from 'react';
import publicSeoService, { PublicDirectoryFilters, PublicDirectoryResult } from '../../services/publicSeoService';
import type { SeoInventory } from '../../seo/types';

/**
 * The national inventory aggregate is the same payload for every location and
 * service hub page, so it is fetched once per page load and shared. Without
 * this, walking from a state page to a city page to a service page would
 * re-download the whole aggregate three times.
 */
let inventoryPromise: Promise<SeoInventory | null> | null = null;

export function loadSeoInventory(): Promise<SeoInventory | null> {
  if (!inventoryPromise) {
    inventoryPromise = publicSeoService.getSeoInventory().catch(() => null);
  }
  return inventoryPromise;
}

/** Test seam — clears the module-level cache between cases. */
export function resetSeoInventoryCache(): void {
  inventoryPromise = null;
}

export function useSeoInventory(): { inventory: SeoInventory | null; loading: boolean } {
  const [inventory, setInventory] = useState<SeoInventory | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    loadSeoInventory().then((data) => {
      if (cancelled) return;
      setInventory(data);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return { inventory, loading };
}

const EMPTY_DIRECTORY: PublicDirectoryResult = { total: 0, stylists: [] };

export function usePublicDirectory(
  filters: PublicDirectoryFilters,
  enabled = true
): { directory: PublicDirectoryResult; loading: boolean } {
  const [directory, setDirectory] = useState<PublicDirectoryResult>(EMPTY_DIRECTORY);
  const [loading, setLoading] = useState(enabled);
  const key = JSON.stringify(filters);

  useEffect(() => {
    if (!enabled) {
      setDirectory(EMPTY_DIRECTORY);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    publicSeoService.getPublicDirectory(JSON.parse(key)).then((data) => {
      if (cancelled) return;
      setDirectory(data);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [key, enabled]);

  return { directory, loading };
}
